import PropTypes from "prop-types";
import {
  FilterSectionWrapper,
  FilterSectionHeader,
  AllCheckboxItem,
  CheckboxListItem,
  SearchableFilterContainer,
  FilterListContainer,
  EmptyStateMessage,
} from "./FilterSection";

// Helper function to check if all items are checked
const areAllItemsChecked = (checkedItems, items) => 
  items.length > 0 && items.every((item) => checkedItems.includes(item));

// Helper function to check if some items are checked
const areSomeItemsChecked = (checkedItems, items) =>
  items.some((item) => checkedItems.includes(item)) &&
  !items.every((item) => checkedItems.includes(item));

const FilterSection = ({
  id,
  isOpen,
  onClick,
  title,
  count,
  items,
  checkedItems,
  onToggle,
  onAllToggle,
  onSearch,
  selectAllFlag,
  onUnselect,
  placeholder,
  searchValue,
  helperText,
  onKeyDown,
  isSearchable = false,
  filteredItems = null,
  allItemsChecked,
  someItemsChecked,
  showEmptyState = false,
}) => {
  const displayItems = filteredItems || items;
  
  const checkboxItems = (
    <>
      <AllCheckboxItem onClick={onAllToggle} checked={allItemsChecked} indeterminate={someItemsChecked} />
      {displayItems.filter((item) => item !== "").map((item) => (
        <CheckboxListItem key={item} item={item} isChecked={checkedItems.includes(item)} onClick={onToggle(item)} />
      ))}
    </>
  );

  const containerProps = { isOpen, searchPlaceholder: placeholder, onSearchChange: onSearch, selectAllFlag, onUnselectClick: onUnselect, searchValue, helperText, onKeyDown };
  
  const renderFilterContent = () => {
    if (isSearchable) {
      return <SearchableFilterContainer {...containerProps}>{checkboxItems}</SearchableFilterContainer>;
    }
    return <FilterListContainer isOpen={isOpen}>{checkboxItems}</FilterListContainer>;
  };

  return (
    <FilterSectionWrapper id={id}>
      <FilterSectionHeader isOpen={isOpen} onClick={onClick} title={title} count={count} />
      {showEmptyState && !isSearchable ? (
        <FilterListContainer isOpen={isOpen}>
          <EmptyStateMessage message={`No ${title} Available`} />
        </FilterListContainer>
      ) : !showEmptyState && (
        renderFilterContent()
      )}
    </FilterSectionWrapper>
  );
};

FilterSection.propTypes = {
  id: PropTypes.string,
  isOpen: PropTypes.bool,
  onClick: PropTypes.func,
  title: PropTypes.string,
  count: PropTypes.string,
  items: PropTypes.array,
  checkedItems: PropTypes.array,
  onToggle: PropTypes.func,
  onAllToggle: PropTypes.func,
  onSearch: PropTypes.func,
  selectAllFlag: PropTypes.bool,
  onUnselect: PropTypes.func,
  placeholder: PropTypes.string,
  searchValue: PropTypes.string,
  helperText: PropTypes.string,
  onKeyDown: PropTypes.func,
  isSearchable: PropTypes.bool,
  filteredItems: PropTypes.array,
  allItemsChecked: PropTypes.bool,
  someItemsChecked: PropTypes.bool,
  showEmptyState: PropTypes.bool,
};

export const OEMDrawerFilterOem = ({
  categoryObjectList,
  modelObjectList,
  countryObjectList,
  ECUTypesObjectList,
  apiObjectList,
  channelOpen,
  statusOpen,
  categoryOpen,
  modelOpen,
  ecuOpen,
  countryOpen,
  ApiLevelOpen,
  paymentOpen,
  channelListchecked,
  statusListchecked,
  categoryListChecked,
  modelListChecked,
  ecuListchecked,
  countryListchecked,
  apiLevelListchecked,
  paymentListchecked,
  searchApiLevel,
  selectAllCategoryFlag,
  selectAllModelFlag,
  selectAllCountryFlag,
  selectAllECUFlag,
  selectAllApiFlag,
  channelType,
  statusArray,
  paymentArray,
  handleChannelClick,
  handleStatusClick,
  handleCategoryClick,
  handleModelClick,
  handleECUClick,
  handleCountryClick,
  handleApiLevelClick,
  handlePaymentClick,
  handleChannelToggle,
  handleStatusToggle,
  handleCategoryToggle,
  handleModelToggle,
  handleECUToggle,
  handleCountryToggle,
  handleAPILevelToggle,
  handlePaymentToggle,
  handleAllChannelsToggle,
  handleAllStatusToggle,
  handleAllCategoryToggle,
  handleAllModelToggle,
  handleAllEcuToggle,
  handleAllCountryToggle,
  handleAllApilevelToggle,
  handleAllPaymentToggle,
  handleUnselectCategory,
  handleUnselectModel,
  handleUnselectECU,
  handleUnselectCountry,
  handleUnselectApiLevel,
  handleCategorySearch,
  handleModelSearch,
  handleECUSearch,
  handleCountrySearch,
  handleApiLevelSearch,
  filteredCategory,
  filteredModel,
  filteredECUTypes,
  filteredCountry,
  filteredApiLevel,
}) => {
  // Configuration for simple filters (no search)
  const simpleFilters = [
    {
      id: "channel",
      isOpen: channelOpen,
      onClick: handleChannelClick,
      title: "Channel Types",
      items: channelType,
      checkedItems: channelListchecked,
      onToggle: handleChannelToggle,
      onAllToggle: handleAllChannelsToggle,
    },
    {
      id: "status",
      isOpen: statusOpen,
      onClick: handleStatusClick,
      title: "Status",
      items: statusArray,
      checkedItems: statusListchecked,
      onToggle: handleStatusToggle,
      onAllToggle: handleAllStatusToggle,
    },
    {
      id: "payment",
      isOpen: paymentOpen,
      onClick: handlePaymentClick,
      title: "Payments",
      items: paymentArray,
      checkedItems: paymentListchecked,
      onToggle: handlePaymentToggle,
      onAllToggle: handleAllPaymentToggle,
    },
  ];

  // Configuration for searchable filters
  const searchableFilters = [
    {
      id: "category",
      isOpen: categoryOpen,
      onClick: handleCategoryClick,
      title: "Categories",
      items: Object.keys(categoryObjectList),
      filteredItems: filteredCategory,
      checkedItems: categoryListChecked,
      onToggle: handleCategoryToggle,
      onAllToggle: handleAllCategoryToggle,
      onSearch: handleCategorySearch,
      selectAllFlag: selectAllCategoryFlag,
      onUnselect: handleUnselectCategory,
      placeholder: "Search for Category",
      countCalculation: () =>
        `${categoryListChecked.length}/${
          Object.keys(categoryObjectList).filter((item) => item !== "").length
        }`,
    },
    {
      id: "model",
      isOpen: modelOpen,
      onClick: handleModelClick,
      title: "Models",
      items: Object.keys(modelObjectList),
      filteredItems: filteredModel,
      checkedItems: modelListChecked,
      onToggle: handleModelToggle,
      onAllToggle: handleAllModelToggle,
      onSearch: handleModelSearch,
      selectAllFlag: selectAllModelFlag,
      onUnselect: handleUnselectModel,
      placeholder: "Search for Model",
      countCalculation: () =>
        `${modelListChecked.length}/${
          Object.keys(modelObjectList).filter((item) => item !== "").length
        }`,
    },
    {
      id: "country",
      isOpen: countryOpen,
      onClick: handleCountryClick,
      title: "Country",
      items: countryObjectList,
      filteredItems: filteredCountry,
      checkedItems: countryListchecked,
      onToggle: handleCountryToggle,
      onAllToggle: handleAllCountryToggle,
      onSearch: handleCountrySearch,
      selectAllFlag: selectAllCountryFlag,
      onUnselect: handleUnselectCountry,
      placeholder: "Search for Country",
      countCalculation: () =>
        `${countryListchecked.length}/${countryObjectList.length}`,
    },
    {
      id: "ecu",
      isOpen: ecuOpen,
      onClick: handleECUClick,
      title: "ECU Type",
      items: ECUTypesObjectList || [],
      filteredItems: filteredECUTypes,
      checkedItems: ecuListchecked,
      onToggle: handleECUToggle,
      onAllToggle: handleAllEcuToggle,
      onSearch: handleECUSearch,
      selectAllFlag: selectAllECUFlag,
      onUnselect: handleUnselectECU,
      placeholder: "Search for ECU Type",
      countCalculation: () =>
        `${ecuListchecked.length}/${ECUTypesObjectList?.length || 0}`,
      showEmptyState: ECUTypesObjectList === undefined || ECUTypesObjectList.length === 0,
    },
    {
      id: "apiLevel",
      isOpen: ApiLevelOpen,
      onClick: handleApiLevelClick,
      title: "API Level",
      items: apiObjectList,
      filteredItems: filteredApiLevel,
      checkedItems: apiLevelListchecked,
      onToggle: handleAPILevelToggle,
      onAllToggle: handleAllApilevelToggle,
      onSearch: handleApiLevelSearch,
      selectAllFlag: selectAllApiFlag,
      onUnselect: handleUnselectApiLevel,
      placeholder: "Search for API",
      searchValue: searchApiLevel,
      helperText:
        apiObjectList.length > 0 ? "Greater than or Equal to" : undefined,
      onKeyDown: (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
        }
      },
      countCalculation: () =>
        `${apiLevelListchecked.length}/${apiObjectList?.length || 0}`,
    },
  ];

  return (
    <div>
      {/* Render simple filters */}
      {simpleFilters.map((filter) => (
        <FilterSection
          key={filter.id}
          {...filter}
          allItemsChecked={filter.checkedItems.length === filter.items.length}
          someItemsChecked={
            filter.checkedItems.length > 0 &&
            filter.checkedItems.length < filter.items.length
          }
        />
      ))}

      {/* Render searchable filters */}
      {searchableFilters.map((filter) => {
        const isEcuWithEmptyState = filter.id === "ecu" && filter.showEmptyState;
        const isEcuWithData = filter.id === "ecu" && !filter.showEmptyState;

        if (filter.id === "ecu" && !isEcuWithData && !isEcuWithEmptyState) {
          return null;
        }

        return (
          <FilterSection
            key={filter.id}
            {...filter}
            count={filter.countCalculation()}
            isSearchable={!isEcuWithEmptyState}
            allItemsChecked={
              isEcuWithEmptyState
                ? false
                : areAllItemsChecked(
                    filter.checkedItems,
                    filter.filteredItems
                  )
            }
            someItemsChecked={
              isEcuWithEmptyState
                ? false
                : areSomeItemsChecked(
                    filter.checkedItems,
                    filter.filteredItems
                  )
            }
          />
        );
      })}
    </div>
  );
};

OEMDrawerFilterOem.propTypes = {
  categoryObjectList: PropTypes.object,
  modelObjectList: PropTypes.object,
  countryObjectList: PropTypes.array,
  ECUTypesObjectList: PropTypes.array,
  apiObjectList: PropTypes.array,
  channelOpen: PropTypes.bool,
  statusOpen: PropTypes.bool,
  categoryOpen: PropTypes.bool,
  modelOpen: PropTypes.bool,
  ecuOpen: PropTypes.bool,
  countryOpen: PropTypes.bool,
  ApiLevelOpen: PropTypes.bool,
  paymentOpen: PropTypes.bool,
  channelListchecked: PropTypes.array,
  statusListchecked: PropTypes.array,
  categoryListChecked: PropTypes.array,
  modelListChecked: PropTypes.array,
  ecuListchecked: PropTypes.array,
  countryListchecked: PropTypes.array,
  apiLevelListchecked: PropTypes.array,
  paymentListchecked: PropTypes.array,
  searchApiLevel: PropTypes.string,
  selectAllCategoryFlag: PropTypes.bool,
  selectAllModelFlag: PropTypes.bool,
  selectAllCountryFlag: PropTypes.bool,
  selectAllECUFlag: PropTypes.bool,
  selectAllApiFlag: PropTypes.bool,
  channelType: PropTypes.array,
  statusArray: PropTypes.array,
  paymentArray: PropTypes.array,
  handleChannelClick: PropTypes.func,
  handleStatusClick: PropTypes.func,
  handleCategoryClick: PropTypes.func,
  handleModelClick: PropTypes.func,
  handleECUClick: PropTypes.func,
  handleCountryClick: PropTypes.func,
  handleApiLevelClick: PropTypes.func,
  handlePaymentClick: PropTypes.func,
  handleChannelToggle: PropTypes.func,
  handleStatusToggle: PropTypes.func,
  handleCategoryToggle: PropTypes.func,
  handleModelToggle: PropTypes.func,
  handleECUToggle: PropTypes.func,
  handleCountryToggle: PropTypes.func,
  handleAPILevelToggle: PropTypes.func,
  handlePaymentToggle: PropTypes.func,
  handleAllChannelsToggle: PropTypes.func,
  handleAllStatusToggle: PropTypes.func,
  handleAllCategoryToggle: PropTypes.func,
  handleAllModelToggle: PropTypes.func,
  handleAllEcuToggle: PropTypes.func,
  handleAllCountryToggle: PropTypes.func,
  handleAllApilevelToggle: PropTypes.func,
  handleAllPaymentToggle: PropTypes.func,
  handleUnselectCategory: PropTypes.func,
  handleUnselectModel: PropTypes.func,
  handleUnselectECU: PropTypes.func,
  handleUnselectCountry: PropTypes.func,
  handleUnselectApiLevel: PropTypes.func,
  handleCategorySearch: PropTypes.func,
  handleModelSearch: PropTypes.func,
  handleECUSearch: PropTypes.func,
  handleCountrySearch: PropTypes.func,
  handleApiLevelSearch: PropTypes.func,
  filteredCategory: PropTypes.array,
  filteredModel: PropTypes.array,
  filteredECUTypes: PropTypes.array,
  filteredCountry: PropTypes.array,
  filteredApiLevel: PropTypes.array,
};
